import { Comment, Static, Text, type ComponentInternalInstance, type VNode } from 'vue';
import {
  copyLocaleCodeForPath,
  defaultLocaleCode,
  getLocaleFromPath,
  localizedIndexablePaths,
  localizedPath,
  normalizePath,
  stripLocaleFromPath,
} from '~/data/locales';

interface TranslationPayload {
  translations: Record<string, string>;
}

type RenderFunction = ((...args: unknown[]) => unknown) & {
  _rc?: boolean;
  __localized?: boolean;
};

const translatedAttributes = [
  'alt',
  'aria-description',
  'aria-label',
  'placeholder',
  'title',
];

// Mirrors scripts/localization-utils.mjs, which leaves these subtrees untranslated.
const ignoredTags = new Set(['code', 'noscript', 'pre', 'script', 'style', 'svg', 'template']);
const ignoredSelector = `${[...ignoredTags].join(', ')}, [translate="no"]`;
const translatableMetaKeys = new Set([
  'description',
  'og:description',
  'og:title',
  'twitter:description',
  'twitter:title',
]);
const canonicalText = (value = '') => value.replace(/\s+/g, ' ').trim();
const COMPONENT = 2 | 4; // ShapeFlags.FUNCTIONAL_COMPONENT | STATEFUL_COMPONENT
const SUSPENSE = 128; // ShapeFlags.SUSPENSE

const isGeneratedEdition = (path: string) =>
  getLocaleFromPath(path).code !== defaultLocaleCode &&
  copyLocaleCodeForPath(path) === defaultLocaleCode;

// Generated editions (every localized page except / and /pricing) are prerendered
// in English; scripts/generate-localized-editions.mjs then translates the HTML and
// embeds that page's own dictionary (#workspace369-localized-copy). The client
// still runs the English app, so this plugin translates every component's render
// through the same dictionary before it reaches the DOM. Hydration then matches
// the translated HTML instead of rewriting it to English (and back, via the
// observer below, which is now only a fallback for DOM Vue doesn't render).
export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter();
  let translations: Record<string, string> = {};
  let hasTranslations = false;
  let localeCode = defaultLocaleCode;
  let applying = false;

  const currentPath = () => window.location.pathname;
  let checkedPath = '';
  let checkedResult = false;
  const shouldTranslatePage = () => {
    const path = currentPath();
    if (path !== checkedPath) {
      checkedPath = path;
      checkedResult = isGeneratedEdition(path);
    }
    return hasTranslations && checkedResult;
  };

  // Only the page's embedded dictionary is used. The whole-locale caches in
  // public/i18n/ (~9 MB each) are build inputs and are never fetched.
  const loadCurrentLocale = () => {
    localeCode = getLocaleFromPath(currentPath()).code;
    translations = {};
    if (isGeneratedEdition(currentPath())) {
      const embedded = document.querySelector<HTMLScriptElement>('#workspace369-localized-copy');
      try {
        if (embedded?.textContent) {
          translations = (JSON.parse(embedded.textContent) as TranslationPayload).translations || {};
        }
      } catch {
        translations = {};
      }
    }
    hasTranslations = Object.keys(translations).length > 0;
  };

  const translateValue = (value: string) => {
    const translation = translations[canonicalText(value)];
    if (!translation) return value;
    const leading = value.match(/^\s*/)?.[0] || '';
    const trailing = value.match(/\s*$/)?.[0] || '';
    return `${leading}${translation}${trailing}`;
  };

  // --- Render-time translation: before hydration and on every later render ---

  // Cached vnodes are reused across renders; translate each one once so a
  // translation is never looked up again as if it were English.
  const translatedVNodes = new WeakSet<object>();

  const translateChildren = (children: unknown[]) => {
    for (let index = 0; index < children.length; index += 1) {
      const child = children[index];
      if (typeof child === 'string') children[index] = translateValue(child);
      else if (Array.isArray(child)) translateChildren(child);
      else if (child && typeof child === 'object') translateVNode(child as VNode);
    }
  };

  const translateVNode = (vnode: VNode) => {
    if (translatedVNodes.has(vnode)) return;
    translatedVNodes.add(vnode);

    const { type, shapeFlag } = vnode;
    if (type === Comment || type === Static) return;
    if (type === Text) {
      if (typeof vnode.children === 'string') vnode.children = translateValue(vnode.children);
      return;
    }
    // Child components translate their own render (see the mixin below).
    if (shapeFlag & COMPONENT) return;
    if (shapeFlag & SUSPENSE) {
      if (vnode.ssContent) translateVNode(vnode.ssContent);
      if (vnode.ssFallback) translateVNode(vnode.ssFallback);
      return;
    }

    if (typeof type === 'string') {
      if (ignoredTags.has(type)) return;
      const props = vnode.props;
      if (props) {
        if (props.translate === 'no') return;
        let translatedProps: Record<string, unknown> | null = null;
        for (const attribute of translatedAttributes) {
          const value = props[attribute];
          if (typeof value !== 'string' || !value) continue;
          const translated = translateValue(value);
          // Copy rather than write through: hoisted props objects are shared.
          if (translated !== value) (translatedProps ||= { ...props })[attribute] = translated;
        }
        if (translatedProps) vnode.props = translatedProps;
      }
      if (typeof vnode.children === 'string') {
        vnode.children = translateValue(vnode.children);
        return;
      }
    }

    if (Array.isArray(vnode.children)) translateChildren(vnode.children);
  };

  const translateRender = (tree: unknown) => {
    if (!shouldTranslatePage()) return tree;
    if (Array.isArray(tree)) translateChildren(tree);
    else if (tree && typeof tree === 'object') translateVNode(tree as VNode);
    return tree;
  };

  loadCurrentLocale();

  if (shouldTranslatePage()) {
    nuxtApp.vueApp.mixin({
      beforeMount(this: { $: ComponentInternalInstance & { render?: RenderFunction } }) {
        const instance = this.$;
        const render = instance.render;
        if (!render || render.__localized) return;
        const localizedRender: RenderFunction = function (this: unknown, ...args: unknown[]) {
          return translateRender(render.apply(this, args));
        };
        localizedRender._rc = render._rc;
        localizedRender.__localized = true;
        instance.render = localizedRender;
      },
    });

    // The prerendered head is already localized (titles shortened, descriptions
    // trimmed, canonical and og:url pointing at this edition). Keep it instead of
    // re-applying the English useHead() values.
    const initialPath = currentPath();
    const staticTitle = document.title;
    const staticCanonical = document.head
      .querySelector('link[rel="canonical"]')
      ?.getAttribute('href');
    const staticMeta = new Map<string, string>();
    for (const meta of document.head.querySelectorAll<HTMLMetaElement>('meta[name], meta[property]')) {
      const key = (meta.getAttribute('name') || meta.getAttribute('property') || '').toLowerCase();
      if (translatableMetaKeys.has(key) || key === 'og:url' || key === 'og:locale') {
        staticMeta.set(key, meta.content);
      }
    }
    const staticSchemas = new Map<string, string>();
    for (const script of document.head.querySelectorAll<HTMLScriptElement>(
      'script[type="application/ld+json"][data-hid]',
    )) {
      staticSchemas.set(script.dataset.hid || '', script.innerHTML);
    }
    injectHead().hooks.hook('tags:afterResolve', ({ tags }) => {
      if (currentPath() !== initialPath) return;
      for (const tag of tags) {
        if (tag.tag === 'title') {
          if (staticTitle) tag.textContent = staticTitle;
        } else if (tag.tag === 'meta' && tag.props) {
          const key = String(tag.props.name || tag.props.property || '').toLowerCase();
          const content = staticMeta.get(key);
          if (content !== undefined) tag.props.content = content;
        } else if (tag.tag === 'link' && tag.props?.rel === 'canonical') {
          if (staticCanonical) tag.props.href = staticCanonical;
        } else if (tag.tag === 'script' && tag.key) {
          const schema = staticSchemas.get(tag.key);
          if (schema !== undefined) tag.innerHTML = schema;
        }
      }
    });
  }

  // --- DOM fallback for anything that reaches the page outside a render ---

  const isIgnored = (node: Node) =>
    node.parentElement?.closest(ignoredSelector) !== null;

  const localizeAnchor = (element: HTMLAnchorElement) => {
    const href = element.getAttribute('href');
    if (!href || /^(?:https?:\/\/|mailto:|tel:|javascript:|#)/i.test(href)) return;
    const path = normalizePath(href.split(/[?#]/)[0]);
    if (!localizedIndexablePaths.has(path)) return;
    const localized = localizedPath(href, localeCode);
    // setAttribute queues a mutation record even when the value is unchanged,
    // which would re-trigger the observer below forever — only write on change.
    if (localized !== href) element.setAttribute('href', localized);
  };

  const translateElement = (element: Element) => {
    if (element.matches(ignoredSelector) || element.closest(ignoredSelector)) return;
    for (const attribute of translatedAttributes) {
      const value = element.getAttribute(attribute);
      if (!value) continue;
      const translated = translateValue(value);
      if (translated !== value) element.setAttribute(attribute, translated);
    }

    if (element instanceof HTMLMetaElement) {
      const key = (element.name || element.getAttribute('property') || '').toLowerCase();
      if (translatableMetaKeys.has(key)) {
        const translated = translateValue(element.content);
        if (translated !== element.content) element.content = translated;
      }
    }
    if (element instanceof HTMLAnchorElement) localizeAnchor(element);
  };

  const translateTextNode = (node: Node) => {
    // Same feedback-loop guard as translateElement: nodeValue assignment queues a
    // characterData record even when unchanged, so only write on an actual change.
    const value = node.nodeValue || '';
    const translated = translateValue(value);
    if (translated !== value) node.nodeValue = translated;
  };

  const translateTree = (root: Node) => {
    if (!shouldTranslatePage()) return;
    applying = true;
    try {
      if (root.nodeType === Node.TEXT_NODE && !isIgnored(root)) {
        translateTextNode(root);
        return;
      }
      if (root instanceof Element) translateElement(root);

      const walker = document.createTreeWalker(
        root,
        NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
      );
      let node = walker.nextNode();
      while (node) {
        if (node.nodeType === Node.TEXT_NODE && !isIgnored(node)) {
          translateTextNode(node);
        } else if (node instanceof Element) {
          translateElement(node);
        }
        node = walker.nextNode();
      }
    } finally {
      applying = false;
    }
  };

  router.beforeEach((to, from) => {
    // Hash/query changes keep the same embedded page dictionary. Assigning the
    // same-page URL here can loop between native hash navigation and cancellation.
    if (to.path === from.path) return true;
    const destinationPath = normalizePath(stripLocaleFromPath(to.path));
    const currentLocale = getLocaleFromPath(from.path);
    const destinationLocale = getLocaleFromPath(to.path);
    const crossesGeneratedEdition =
      localizedIndexablePaths.has(destinationPath) &&
      (currentLocale.code !== defaultLocaleCode || destinationLocale.code !== defaultLocaleCode);

    if (!crossesGeneratedEdition) return true;
    window.location.assign(to.fullPath);
    return false;
  });

  const observer = new MutationObserver((mutations) => {
    if (applying || !shouldTranslatePage()) return;
    for (const mutation of mutations) {
      if (mutation.type === 'characterData') translateTree(mutation.target);
      if (mutation.type === 'attributes' && mutation.target instanceof Element) {
        translateElement(mutation.target);
      }
      for (const node of mutation.addedNodes) translateTree(node);
    }
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: [...translatedAttributes, 'content', 'href'],
    characterData: true,
    childList: true,
    subtree: true,
  });

  nuxtApp.hook('app:mounted', () => translateTree(document.documentElement));
  nuxtApp.hook('page:finish', () => {
    loadCurrentLocale();
    translateTree(document.documentElement);
  });
});
